import { CSSProperties, FunctionComponent } from "react";

import { ReactComponent as ChevronDoubleRight } from "@assets/icons/chevronDoubleRight.svg";

import { SvgIcon } from "@/components/Icon/SvgIcon";
import { classNames } from "@/utils/classNames";

export interface CollapseGrabberProps {
	/** Which edge of the (relatively-positioned) parent the pill rides on. */
	edge: "left" | "right";
	/** Whether the associated column/panel is currently collapsed — drives the chevron direction. */
	collapsed: boolean;
	/** Toggle collapse/expand. Ignored while `disabled`. */
	onToggle: () => void;
	/** Greyed-out, non-interactive (e.g. no lead selected yet). */
	disabled?: boolean;
	/** Accessible label + native tooltip (already localized by the caller). */
	ariaLabel?: string;
	/** Extra classes for per-surface overrides (vertical offset, z-index). Applied last. */
	className?: string;
	style?: CSSProperties;
	testId?: string;
}

/**
 * Unified collapse/expand "grabber" (PREC-3596): a small rounded pill that rides on
 * the edge of a collapsible column or panel, vertically centered, with a
 * double-chevron that flips to point toward the action (expand vs collapse).
 *
 * Shared by the Messages lead-card column + lead-detail rail, the
 * List/Contacts/Global-Search/Tracker lead-detail panels, and the Chrome
 * extension. The parent must be `position: relative` (or `fixed`/`absolute`) and
 * must NOT clip overflow-x, so the pill's outer half can overhang the edge.
 */
export const CollapseGrabber: FunctionComponent<CollapseGrabberProps> = ({
	edge,
	collapsed,
	onToggle,
	disabled = false,
	ariaLabel,
	className,
	style,
	testId,
}) => {
	// The base asset points right (»). It should point right when expanding a
	// right-edge grabber (column collapsed → pull it back out) or collapsing a
	// left-edge one (panel open → push it off to the right); left (« = rotated
	// 180°) in the mirror cases. This reduces to a single parity check.
	const pointsRight = (edge === "right") === collapsed;

	return (
		<button
			aria-label={ariaLabel}
			className={classNames(
				"absolute top-1/2 z-20 flex h-12 w-[22px] -translate-y-1/2 items-center justify-center rounded-lg border bg-white shadow-md transition-all duration-200",
				edge === "right" ? "right-[-11px]" : "left-[-11px]",
				disabled
					? "cursor-not-allowed border-gray-100 text-gray-300 shadow-none"
					: "cursor-pointer border-gray-200 text-text-meta hover:border-brand-light hover:text-brand-light hover:shadow-lg",
				className
			)}
			data-testid={testId}
			disabled={disabled}
			title={ariaLabel}
			type="button"
			onClick={disabled ? undefined : onToggle}
		>
			<SvgIcon
				className={classNames("h-[15px] w-[15px]", pointsRight ? "" : "rotate-180")}
				svgIcon={ChevronDoubleRight}
			/>
		</button>
	);
};
